import { useState, useCallback } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

export interface PersonaReaction {
  relevanceScore: number;
  emotionalResponse: string;
  likelyAction: string;
  attentionGrabber: string;
  objections: string[];
  improvements: string[];
  overallVerdict: "strong" | "moderate" | "weak";
}

export interface PersonaVariant {
  angle: string;
  content: string;
  rationale: string;
}

interface UsePersonaReactionResult {
  isLoading: boolean;
  error: string | null;
  reaction: PersonaReaction | null;
  simulateReaction: (
    projectId: string,
    content: string,
    contentType?: string
  ) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

interface UsePersonaVariantsResult {
  isLoading: boolean;
  error: string | null;
  variants: PersonaVariant[];
  generateVariants: (
    projectId: string,
    content: string,
    contentType?: string,
    variantCount?: number
  ) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

/**
 * Hook for simulating a persona's reaction to marketing content
 */
export function usePersonaReaction(): UsePersonaReactionResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reaction, setReaction] = useState<PersonaReaction | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setReaction(null);
  }, []);

  const simulateReaction = useCallback(
    async (
      projectId: string,
      content: string,
      contentType?: string
    ): Promise<void> => {
      if (!projectId) {
        setError("Project ID is required");
        return;
      }
      if (!content) {
        setError("Content to test is required");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const functions = getFunctions();
        const simulateFunction = httpsCallable<
          { projectId: string; content: string; contentType?: string },
          { success: boolean; reaction: PersonaReaction }
        >(functions, "simulatePersonaReaction");

        const result = await simulateFunction({
          projectId,
          content,
          contentType,
        });
        setReaction(result.data.reaction);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to simulate persona reaction";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    reaction,
    simulateReaction,
    clearError,
    reset,
  };
}

/**
 * Hook for generating persona-optimized content variants
 */
export function usePersonaVariants(): UsePersonaVariantsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [variants, setVariants] = useState<PersonaVariant[]>([]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setVariants([]);
  }, []);

  const generateVariants = useCallback(
    async (
      projectId: string,
      content: string,
      contentType?: string,
      variantCount?: number
    ): Promise<void> => {
      if (!projectId) {
        setError("Project ID is required");
        return;
      }
      if (!content) {
        setError("Content to generate variants for is required");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const functions = getFunctions();
        const variantsFunction = httpsCallable<
          {
            projectId: string;
            content: string;
            contentType?: string;
            variantCount?: number;
          },
          { success: boolean; variants: PersonaVariant[] }
        >(functions, "simulatePersonaVariants");

        const result = await variantsFunction({
          projectId,
          content,
          contentType,
          variantCount,
        });
        setVariants(result.data.variants);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to generate persona variants";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    variants,
    generateVariants,
    clearError,
    reset,
  };
}
